import type { SurfaceCopy } from './surfaceTypes'

export const surfaceEn: SurfaceCopy = {
  pages: {
    continueLearning: 'Continue Learning',
    keepLearning: 'Keep Learning',
    practice: 'Practice',
    practiceSubtitle: 'One-off stories and drills — for a text that stays in your library, use Reading.',
    settings: 'Settings',
    settingsSubtitle: 'Account & preferences',
  },
  actions: {
    startReading: 'Start reading',
    browseReading: 'Find something to read',
    startRefresh: 'Start refreshing',
    addWords: 'Add words',
    startPractice: 'Start practice',
    cancel: 'Cancel',
    saved: 'Saved',
    readyToRefresh: 'Ready to refresh {count} words',
    readyToRefreshOne: 'Ready to refresh 1 word',
  },
  coach: {
    title: 'A note from your coach',
    dismiss: 'Dismiss',
    andWord: 'and',
    confusionCluster:
      'You often mix up {words}. A quick challenge can help sort them out.',
    formGap:
      "You've seen «{surface}» {count} times while reading. {lemma} has forms you haven't met yet{formsHint}.",
    formGapNoLemma:
      "You've seen «{surface}» {count} times while reading. Its base word has forms you haven't met yet{formsHint}.",
    synonymMixup:
      "«{word1}» and «{word2}» mean similar things — let's practice telling them apart.",
    synonymMixupGeneric:
      'These similar words need a closer look — a quick challenge can help.',
    neglectedWord:
      "{word} hasn't had your attention in {days} days. Ready for a quick refresh?",
    neglectedWordGeneric:
      "This word hasn't had your attention in {days} days. Ready for a quick refresh?",
    ctaPractice: 'Create article from these words',
    ctaOpenWord: 'Open word',
    ctaChallenge: 'Try a quick challenge',
  },
  todayPlan: {
    heading: 'Your day',
    refresh: 'Memory refresh',
    newWords: 'New words saved',
    reading: 'Reading',
    practice: 'AI practice',
    allCaughtUp: 'All refreshed for now',
  },
  dashboard: {
    loadFailed: 'Failed to load dashboard',
    continueLearningEyebrow: 'Continue Learning',
    sessionLabelToday: "Today's session",
    sessionLabelComplete: 'Guided session complete',
    headlineResume: 'Pick up where you left off',
    headlineReadyMore: 'Ready for more?',
    headlineWordsReady: '{count} words ready to refresh',
    headlineWordsReadyOne: '1 word ready to refresh',
    headlineMinutesSession: '~{minutes} minute session',
    detailWaiting: 'Your guided session is waiting.',
    detailDailyDone: 'Your daily minimum is done — explore freely.',
    detailWhatNext: 'What would you like to do next?',
    detailAboutMinutes: 'About {minutes} min in your guided plan.',
    detailCaughtUp: "You're caught up — a quick read may be next.",
    guidedSessionMinutes: '~{minutes} min guided session',
    startFailed: 'Could not start your session. Please try again.',
    wordsThisMonth: '+{count} words this month',
    totalInVocabulary: '{count} total in vocabulary',
    wordsRefreshedToday: '{count} words refreshed today',
    wordsRefreshedTodayOne: '1 word refreshed today',
    keepMomentum: 'Keep the momentum going',
    yesterdayMissed: 'Yesterday missed “{word}” — today {correct} correct',
    vocabHeading: 'Vocabulary',
    statusNew: 'New',
    statusLearning: 'Learning',
    statusReview: 'Review',
    statusMastered: 'Mastered',
    dueForReview: 'Due for review',
    viewAllWords: 'View all words',
    streak: 'Streak',
    dayInARow: 'day in a row',
    daysInARow: 'days in a row',
    studiedToday: '✓ You studied today — keep it up!',
    keepStreakAlive: 'Study today to keep your streak alive.',
    startStreak: 'Start studying to build your streak.',
    latestWin: 'Latest win',
    catalogExploreHint: '— explore the Language Database when you want to browse',
    feedbackCta: 'Idea or bug? Tell us',
  },
  achievementTitles: {
    FIRST_SESSION: 'First steps',
    WEEK_STREAK: 'Week warrior',
    WORDS_50: 'Word collector',
    WORDS_100: 'Growing lexicon',
    WORDS_250: 'Word builder',
    WORDS_500: 'Vocabulary vault',
    READING_STREAK_5: 'Reading rhythm',
    COMEBACK: 'Welcome back',
    FIRST_MASTERED: 'First mastery',
    CHALLENGE_PERFECT: 'Challenge ace',
  },
  aiCredits: {
    heading: 'Daily AI credits',
    short: 'AI left today',
    ofLimit: '{used} used · {limit} daily limit',
    compact: '{remaining} of {limit} AI credits left today',
    hint: 'Covers article generation, practice stories, and comprehension questions. Word explanations from cache are free.',
    costPractice: 'Story or lesson: {cost}',
    costArticle: 'Personal article: {cost}',
    costComprehension: 'Reading reflection questions: {cost}',
    costComprehensionCheck: 'Coach feedback per answer: {cost}',
    reset: 'Resets at {time} (UTC). Changing timezone does not reset credits.',
    exceeded: 'Daily AI credits used up. They reset at midnight UTC.',
    phraseLimitExceeded: 'Daily phrase explanation limit reached. Already explained phrases still work — try again tomorrow.',
    phraseQuotaCompact: '{remaining} of {limit} new phrase explanations left today',
  },
  reading: {
    pickedForYou: 'Picked for you',
    tabForYou: 'For you',
    tabLibrary: 'Library',
    noRecommendationTitle: 'No recommendation yet',
    noRecommendationHint: 'Open the library tab or generate a personal article from your words.',
    filterAll: 'All',
    filterUnread: 'Unread',
    filterRead: 'Read',
    generatePersonalizedTitle: 'Article for your vocabulary',
    generatePersonalizedHint:
      'AI weaves up to {max} items from your vocabulary into a short personal read.',
    generatePersonalizedPrivate: 'Only you can see this article — it is not shared with other learners.',
    generatePersonalizedWordCount: '{count} from your vocabulary',
    generatePersonalizedWordCountMax: 'Up to {max} from your vocabulary',
    shuffleWords: 'Shuffle',
    generatePersonalizedNoWords: 'Save words or phrases while reading to unlock personal articles.',
    generatePersonalizedCta: 'Generate article for my vocabulary',
    generatePersonalizedLoading: 'Writing your article…',
    generatePersonalizedFailed: 'Could not generate an article. Please try again.',
    generatePersonalizedFieldLevel: 'Level',
    generatePersonalizedFieldDuration: 'Reading time',
    generatePersonalizedFieldTopics: 'Topics',
    generatePersonalizedTopicsHint:
      'Choose up to 2 topics. Leave empty to pick from your settings preferences.',
    durationMinutes: '{minutes} min',
    durationMinutesApprox: '~{minutes} min',
    articleGeneratedTitle: 'Article saved to your Library',
    articleGeneratedBody:
      'Your personal article is ready. You can always find it again in the Library tab.',
    articleGeneratedStart: 'Start reading',
    articleGeneratedLibrary: 'Go to Library',
    levelBadge: '{level} level',
    minRead: '{minutes} min read',
  },
  recommendation: {
    whyArticle: 'Why this article?',
    dueWords: 'Due words',
  },
  learn: {
    stepOf: 'Step {current} of {total}',
    readPrefix: 'Read:',
    readThenFinish: 'Read this article, then mark it as finished to continue your session.',
    nothingScheduled: 'Nothing scheduled today',
    startRefresh: 'Start refresh',
    startChallenge: 'Start challenge',
    backToToday: 'Back to Today',
  },
  practice: {
    generateStory: 'Generate Story',
    generateLesson: 'Generate Lesson',
    newLesson: 'New lesson',
    storyDesc: 'A short story using your vocabulary',
    lessonDesc: 'An interactive lesson with warm-up, practice, and challenge',
    selectToStart: 'Select vocabulary to get started.',
    wordLimit: '{selected} / {max} items',
    wordLimitHint: 'Up to {max} items per story or lesson (based on your level)',
    wordLimitReached: 'Maximum {max} items — deselect one to add another',
    pickRandom: 'Pick {count} random',
    pickWords: 'Pick vocabulary to practice',
    deselectAll: 'Deselect all',
    articleTipTitle: 'Want a text you can keep?',
    articleTipBody:
      'Generate a personalized article in Reading — it stays in your library and you can tap words while you read.',
    articleTipCta: 'Go to Reading',
  },
  settingsChrome: {
    tabPreferences: 'Preferences',
    tabAppearance: 'Appearance',
    tabGoals: 'Daily Goals',
    tabNotifications: 'Notifications',
    tabFeedback: 'Feedback',
    tabSecurity: 'Security',
    tabAccount: 'Account',
    preferencesTitle: 'Preferences',
    preferencesDescription: 'Language, level, and learning preferences.',
    savePreferences: 'Save preferences',
    goalsTitle: 'Daily Learning Goals',
    goalsDescription: 'Set your daily targets with sliders or exact numbers.',
    goalsNewWords: 'New words per day',
    goalsReviews: 'Reviews per day',
    goalsArticles: 'Articles per day',
    goalsPractice: 'AI practice per day',
    goalsPracticeProHint:
      'AI practice is included with Pro. Your goal is saved and will apply when you upgrade.',
    goalsPracticeOff: 'Off',
    saveGoals: 'Save goals',
    notificationsTitle: 'Notifications',
    notificationsDescription: 'Email and reminder preferences.',
    notificationsComingSoon: 'Coming soon',
    notificationsComingSoonHint:
      'Weekly email digest is in development. We will enable this toggle when delivery is ready.',
    preferredTopics: 'Topics you enjoy',
    preferredTopicsHint: 'Used to personalize reading recommendations',
    includeQuickChallenge: 'Include quick challenge in guided session',
    includeQuickChallengeHint: 'Adds a short vocabulary check to your daily session',
    defaultReviewMode: 'Default review mode',
    defaultReviewModeHint: 'Used when you start a standalone refresh session',
    targetLevel: 'Target English Level',
    targetLevelHint: 'Articles and practice will match this level.',
    sessionLength: 'Guided session length',
    sessionLengthLabel: 'Guided session length: {minutes} min',
    timezone: 'Timezone',
    timezoneHint: 'Set when you joined Lexio. Daily AI limits reset at midnight UTC regardless of timezone.',
    securitySignOutTitle: 'Sign out',
    securitySignOutHint: 'Log out of Lexio on this device.',
    changePasswordTitle: 'Change Password',
    changePasswordDescription: 'Update the password you use to sign in.',
    accountTitle: 'Account',
    accountDescription: 'Manage your account data.',
    backToSettings: 'Back to Settings',
    goBack: 'Back',
    loadFailed: 'Failed to load settings',
  },
}
